import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { cartao, cor, texto } from '#components/finapp/estilo';
import type { ResumoDoMes } from '#components/finapp/motor/mesFinanceiro';

type Props = {
  resumo: ResumoDoMes;
  /**
   * Empilha as três colunas (uma coluna vira uma linha) em vez de deixá-las
   * lado a lado — usado só no celular.
   *
   * Vem de `useResponsive()` em `Inicio.tsx`. Lado a lado, as três colunas
   * ficam com ~90px cada num telefone — o rótulo quebra em duas linhas e o
   * valor encosta no vizinho.
   */
  estreito?: boolean;
};

/**
 * Separação entre colunas: só ESPAÇO, nenhuma régua.
 *
 * ⛔ Havia aqui um `borderRight: 1px solid` por coluna. Saiu pela regra nº 1
 * das 6 medidas no MULTICAP — separar por contraste/espaço, nunca por borda,
 * porque borda repetida vira malha e malha lê-se como aperto. O respiro agora
 * vem do `gap` da linha, e é ele que deve crescer se as colunas parecerem
 * espremidas.
 */
const gapEntreColunas = 32;

/**
 * As três colunas do "livre pra decidir" (`/inicio`, logo abaixo do Herói —
 * `inicio/Heroi.tsx`) — quanto ENTROU, quanto foi GASTO e quanto foi
 * GUARDADO — seguidas do bloco "não é gasto": a resposta a "cadê o resto do
 * dinheiro?" quando a saída bruta do mês é muito maior que o gasto real
 * (medido em agosto/2026: ~R$244 mil de saída × ~R$18 mil de gasto — ver
 * `estudos/app-financeiro-fork-actual-2026-09-05.md` §8.1b).
 *
 * ⚠️ `data-testid="tres-colunas"` fica no `<View>` que É literalmente as três
 * colunas (a linha `flexDirection: 'row'`), não num wrapper maior por fora —
 * é esse o elemento cujo `flexDirection` a Task 17 troca para `'column'` no
 * celular (`planos/…`, Task 17, Steps 1 e 3). O bloco "não é gasto" fica
 * FORA dessa linha, como um segundo filho do cartão externo — assim ele
 * nunca disputa `flex: 1` com as três colunas nem precisa de truque de
 * `flexWrap` pra "cair pra linha de baixo".
 *
 * ⚠️⚠️ `resumo.naoEhGasto.naoClassificado` TEM que aparecer aqui — é
 * obrigação herdada da Task 7 (fix round 2, Finding 5; ver o JSDoc de
 * `ResumoDoMes.naoEhGasto.naoClassificado` em `motor/mesFinanceiro.ts`).
 * Esse balde existe porque uma transferência com as pernas em MESES
 * diferentes fica neutra no `livre` (o motor se recusa a supor "aporte" sem
 * saber se a conta da irmã é fora do orçamento); se ele nunca aparecer na
 * tela, o `livre` volta a superestimar em silêncio — o oposto do que a Task
 * 7 corrigiu. Por isso ele entra na condição de exibição
 * (`transferencias + empresa + naoClassificado > 0`, não só os dois
 * primeiros) E ganha um rótulo PRÓPRIO ("não identificado") — nunca é
 * somado silenciosamente à transferência: é dinheiro que saiu e que o app
 * não sabe classificar, e dizer isso é o ponto do balde.
 */
export function TresColunas({ resumo, estreito = false }: Props) {
  const { t } = useTranslation();

  // "Gastei" inclui a fixa realizada (a conta de luz) — a separação
  // fixa × variável é assunto de outro bloco da `/inicio` (ainda não
  // construído), não desta coluna. Ver o Step 1 do brief da Task 9.
  const gastei = resumo.gastosVariaveis + resumo.fixasRealizadas;

  const { transferencias, empresa, naoClassificado } = resumo.naoEhGasto;
  const temNaoEhGasto = transferencias + empresa + naoClassificado > 0;

  const segmentosNaoEhGasto: string[] = [];
  if (transferencias > 0) {
    segmentosNaoEhGasto.push(
      `${t('transferência')} ${integerToCurrency(transferencias)}`,
    );
  }
  if (empresa > 0) {
    segmentosNaoEhGasto.push(`${t('empresa')} ${integerToCurrency(empresa)}`);
  }
  if (naoClassificado > 0) {
    segmentosNaoEhGasto.push(
      `${t('não identificado')} ${integerToCurrency(naoClassificado)}`,
    );
  }

  return (
    <View style={{ ...cartao, gap: 10 }}>
      <View
        data-testid="tres-colunas"
        style={{
          flexDirection: estreito ? 'column' : 'row',
          gap: estreito ? 12 : gapEntreColunas,
        }}
      >
        <View style={{ flex: 1, gap: 4 }}>
          <Text style={texto.rotuloCartao}>
            <Trans>Entrou</Trans>
          </Text>
          <Text
            data-testid="col-entrou"
            style={{ ...texto.valorCartao, color: cor.entrada }}
          >
            {integerToCurrency(resumo.entrou)}
          </Text>
        </View>

        <View style={{ flex: 1, gap: 4 }}>
          <Text style={texto.rotuloCartao}>
            <Trans>Gastei</Trans>
          </Text>
          <Text
            data-testid="col-gastei"
            style={{ ...texto.valorCartao, color: cor.saida }}
          >
            {integerToCurrency(gastei)}
          </Text>
        </View>

        <View style={{ flex: 1, gap: 4 }}>
          <Text style={texto.rotuloCartao}>
            <Trans>Guardei</Trans>
          </Text>
          <Text
            data-testid="col-guardei"
            style={{ ...texto.valorCartao, color: cor.investimento }}
          >
            {integerToCurrency(resumo.aportes)}
          </Text>
        </View>
      </View>

      {temNaoEhGasto && (
        <Text
          data-testid="nao-eh-gasto"
          style={{ ...texto.corpo, color: cor.tintaFraca }}
        >
          {t('não é gasto:')} {segmentosNaoEhGasto.join(' · ')}
        </Text>
      )}
    </View>
  );
}
